import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  type?: 'website' | 'article' | 'service';
  schema?: object | object[];
  keywords?: string;
  noindex?: boolean;
}

export const SEO = ({
  title,
  description,
  canonical = 'https://www.affinityaim.com',
  ogImage = 'https://www.affinityaim.com/og-image.png',
  type = 'website',
  schema,
  keywords,
  noindex = false
}: SEOProps) => {
  useEffect(() => {
    // Set page title
    document.title = title;

    // Helper to update or create meta tag
    const updateMetaTag = (selector: string, attribute: string, content: string) => {
      let element = document.querySelector(selector) as HTMLMetaElement;
      if (!element) {
        element = document.createElement('meta');
        if (selector.includes('property=')) {
          element.setAttribute('property', selector.split('"')[1]);
        } else if (selector.includes('name=')) {
          element.setAttribute('name', selector.split('"')[1]);
        }
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Update or create link tag
    const updateLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };

    // Basic meta tags
    updateMetaTag('meta[name="description"]', 'content', description);
    if (keywords) {
      updateMetaTag('meta[name="keywords"]', 'content', keywords);
    }
    
    // Robots meta tag for noindex
    if (noindex) {
      updateMetaTag('meta[name="robots"]', 'content', 'noindex, nofollow');
    } else {
      // Remove noindex if it was previously set
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (robotsMeta) {
        robotsMeta.remove();
      }
    }

    // Canonical URL
    updateLinkTag('canonical', canonical);

    // Open Graph tags
    updateMetaTag('meta[property="og:title"]', 'content', title);
    updateMetaTag('meta[property="og:description"]', 'content', description);
    updateMetaTag('meta[property="og:url"]', 'content', canonical);
    updateMetaTag('meta[property="og:type"]', 'content', type);
    updateMetaTag('meta[property="og:image"]', 'content', ogImage);

    // Twitter Card tags
    updateMetaTag('meta[name="twitter:card"]', 'content', 'summary_large_image');
    updateMetaTag('meta[name="twitter:title"]', 'content', title);
    updateMetaTag('meta[name="twitter:description"]', 'content', description);
    updateMetaTag('meta[name="twitter:image"]', 'content', ogImage);

    // JSON-LD Structured Data: always include a WebPage entry so every
    // route ships at least one rich-results schema. Page-specific schemas
    // (Article, ContactPage, BreadcrumbList) are appended.
    const webPageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: title,
      description,
      url: canonical,
      inLanguage: "en",
      isPartOf: {
        "@type": "WebSite",
        name: "AffinityAim",
        url: "https://www.affinityaim.com",
      },
      publisher: {
        "@type": "Organization",
        name: "AffinityAim",
        url: "https://www.affinityaim.com",
      },
    };

    const schemaArray: object[] = [webPageSchema];
    if (schema) {
      schemaArray.push(...(Array.isArray(schema) ? schema : [schema]));
    }

    const existingSchemas = document.querySelectorAll('script[data-seo-schema="true"]');
    existingSchemas.forEach((s) => s.remove());

    schemaArray.forEach((schemaObj) => {
      const script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-seo-schema', 'true');
      script.textContent = JSON.stringify(schemaObj);
      document.head.appendChild(script);
    });

    // Cleanup function
    return () => {
      // Remove dynamic schemas when component unmounts
      const schemas = document.querySelectorAll('script[data-seo-schema="true"]');
      schemas.forEach(s => s.remove());
    };
  }, [title, description, canonical, ogImage, type, schema, keywords, noindex]);

  return null;
};
